import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import { useTaskThumbnails } from '../hooks/useTaskThumbnails';
import { useTaskPricing } from '../hooks/useTaskPricing';
import { listMarketplace, MarketplaceItem } from '../lib/callables';

const FALLBACK = 'Uncategorized';

const isEmptyLevel = (v: unknown) => {
  const s = v == null ? '' : String(v).trim();
  return !s || s === FALLBACK;
};

export type RawPhoto = {
  id: string;
  category: string;
  subcategory: string;
  itemType: string;
  taskTitle: string;
  taskId?: string;
  imageUrl: string;
  videoUrl?: string; // trimmedVideoUrl (or fallback) for raw video clips
  durationSeconds?: number; // videoMetadata.duration (seconds), for raw video
  releasedAt?: Date | null; // marketplace.<type>ReleasedAt
  exported?: boolean;
  lastExportedAt?: Date | null;
  credits?: number; // per-item credit cost, resolved from the task's pricing
  unpriced?: boolean; // task has no price for this tier — must not be purchasable
  labelClassIds?: string[];   // raw categoryIds on this photo's labels
  labelCount?: number;        // total labels on this photo
  labelCategories?: string[];     // distinct readable label-categories present on this photo
  labelSubcategories?: string[];  // distinct readable label-subcategories present on this photo
};

type Node = {
  key: string;
  name: string;
  level: number; // 0 cat, 1 sub, 2 item, 3 task
  photoIds: string[];
  children: Node[];
  photos?: RawPhoto[];
  stats?: { subcategories?: number; itemTypes?: number; tasks?: number };
  labelTypes?: number;   // distinct label classes beneath this node
  labelTotal?: number;   // total labels beneath this node
  durationTotal?: number; // summed durationSeconds under this node (videos)
  creditsTotal?: number;  // summed per-item credits under this node (purchase mode)
};

type Filters = { category: string; subcategory: string; itemType: string; task: string };

type Props = {
  photos: RawPhoto[];
  loading: boolean;
  selected: Set<string>;
  onToggleIds: (ids: string[], select: boolean) => void;
  filters: Filters;
  onFilterChange: (f: Filters) => void;
  expanded: Set<string>;
  onToggleExpand: (key: string) => void;
  ownedIds?: Set<string>;
  mode?: 'purchase' | 'library'; // default 'purchase'; in library mode, ownedIds is IGNORED for filtering
  unitLabel?: string; // singular unit for UI copy, default 'photo' → Photos / "N photos"
  showDuration?: boolean; // show duration totals / per-clip times (raw video only)
  labelNameResolver?: (categoryId: string) => string;  // if provided, enables label features (annotated mode)
  resolveLabelCategory?: (categoryId: string) => string;
  resolveLabelSubcategory?: (categoryId: string) => string;
};

const fmtDuration = (s: number) => {
  const total = Math.max(0, Number(s) || 0);
  const m = Math.floor(total / 60);
  const sec = Math.round(total % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

function resolveImageUrl(data: any): string {
  return (
    data.imageUrl || data.photoUrl || data.downloadUrl || data.mediaUrl || data.thumbnailUrl ||
    (Array.isArray(data.imageUrls) ? data.imageUrls[0] : '') ||
    (Array.isArray(data.media) && data.media[0] ? data.media[0].url : '') || ''
  );
}

const VIDEO_EXT = /\.(mp4|mov|webm|m4v|avi)(\?|$|%3F)/i;

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  // Firebase Storage URLs percent-encode the path, so test the decoded form too.
  let decoded = url;
  try { decoded = decodeURIComponent(url); } catch { /* keep raw */ }
  return VIDEO_EXT.test(url) || VIDEO_EXT.test(decoded);
}

function resolveReleasedAt(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') { const d = new Date(value); return isNaN(d.getTime()) ? null : d; }
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

// Subscribe to released raw photos. Exported so the parent can share the data with its footer.
function mapItem(it: MarketplaceItem): RawPhoto {
  return {
    id: it.id,
    category: it.category || FALLBACK,
    subcategory: it.subcategory || FALLBACK,
    itemType: it.itemType || FALLBACK,
    taskTitle: it.taskTitle || FALLBACK,
    taskId: it.taskId || '',
    imageUrl: it.imageUrl || '',
    videoUrl: it.videoUrl,
    durationSeconds: it.durationSeconds,
    credits: it.credits,
    labelClassIds: it.labelClassIds,
    labelCount: it.labelCount,
    releasedAt: resolveReleasedAt(it.releasedAt),
  };
}

function useMarketplaceList(type: 'rawPhoto' | 'annotatedPhoto' | 'rawVideo') {
  const [photos, setPhotos] = React.useState<RawPhoto[]>([]);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    listMarketplace(type)
      .then((res) => {
        if (cancelled) return;
        setPhotos((res.items || []).map(mapItem));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(`Failed to load marketplace (${type})`, err);
        setPhotos([]);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [type]);

  return { photos, loading };
}

export function useReleasedRawPhotos() {
  return useMarketplaceList('rawPhoto');
}

export function useReleasedAnnotatedPhotos() {
  return useMarketplaceList('annotatedPhoto');
}

export function useReleasedRawVideos() {
  return useMarketplaceList('rawVideo');
}

function Check({ checked, indeterminate, onChange, disabled }: { checked: boolean; indeterminate: boolean; onChange: () => void; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <input type="checkbox" ref={ref} checked={checked} disabled={!!disabled}
      onChange={onChange} onClick={(e) => e.stopPropagation()}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', width: 16, height: 16, opacity: disabled ? 0.4 : 1 }} />
  );
}

export default function RawPhotoBrowse({
  photos, loading, selected, onToggleIds, filters, onFilterChange, expanded, onToggleExpand, ownedIds,
  mode = 'purchase',
  unitLabel = 'photo',
  showDuration = false,
  labelNameResolver,
  resolveLabelCategory,
  resolveLabelSubcategory,
}: Props) {
  const { colors } = useTheme();
  const border = colors.border;
  const text = colors.text;
  const secondary = colors.secondary;
  const primary = colors.primary;
  const card = colors.card;

  const unitSingular = unitLabel || 'photo';
  const unitPlural = `${unitSingular}s`;
  const unitPluralCap = unitPlural.charAt(0).toUpperCase() + unitPlural.slice(1);
  const durationEnabled = showDuration && unitLabel === 'video';

  const [labelCatFilter, setLabelCatFilter] = useState<string>('all');
  const [labelSubFilter, setLabelSubFilter] = useState<string>('all');
  const [labelClassFilter, setLabelClassFilter] = useState<string>('all');
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    setPreviewPlaying(false);
    setPreviewTime(0);
  }, [preview]);

  const taskIdsForThumbs = useMemo(
    () =>
      photos
        .map((p) => (typeof p.taskId === 'string' ? p.taskId.trim() : ''))
        .filter((id) => id.length > 0),
    [photos]
  );
  const taskThumbById = useTaskThumbnails(taskIdsForThumbs);

  const availablePhotos = useMemo(
    () => (mode === 'library' ? photos : (ownedIds ? photos.filter((p) => !ownedIds.has(p.id)) : photos)),
    [photos, ownedIds, mode]
  );

  const allVisibleIds = useMemo(() => availablePhotos.map((p) => p.id), [availablePhotos]);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const someSelected = allVisibleIds.some((id) => selected.has(id));
  const headerCheckState = { checked: allSelected, indeterminate: !allSelected && someSelected };
  const toggleAll = () => onToggleIds(allVisibleIds, !allSelected);

  const distinct = (arr: string[]) => Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b));

  const categoryOptions = useMemo(() => distinct(availablePhotos.map((p) => p.category)), [availablePhotos]);
  const subcategoryOptions = useMemo(() => distinct(availablePhotos.filter((p) => filters.category === 'all' || p.category === filters.category).map((p) => p.subcategory)), [availablePhotos, filters.category]);
  const itemTypeOptions = useMemo(() => distinct(availablePhotos.filter((p) => (filters.category === 'all' || p.category === filters.category) && (filters.subcategory === 'all' || p.subcategory === filters.subcategory)).map((p) => p.itemType)), [availablePhotos, filters.category, filters.subcategory]);
  const taskOptions = useMemo(() => distinct(availablePhotos.filter((p) => (filters.category === 'all' || p.category === filters.category) && (filters.subcategory === 'all' || p.subcategory === filters.subcategory) && (filters.itemType === 'all' || p.itemType === filters.itemType)).map((p) => p.taskTitle)), [availablePhotos, filters.category, filters.subcategory, filters.itemType]);

  const photoLabelInfo = useMemo(() => {
    const m = new Map<string, { cats: string[]; subs: string[]; classes: string[] }>();
    photos.forEach((p) => {
      const ids = p.labelClassIds || [];
      const cats = new Set<string>();
      const subs = new Set<string>();
      const classes = new Set<string>();
      ids.forEach((id) => {
        if (resolveLabelCategory) { const c = resolveLabelCategory(id); if (c) cats.add(c); }
        if (resolveLabelSubcategory) { const s = resolveLabelSubcategory(id); if (s) subs.add(s); }
        if (labelNameResolver) { const n = labelNameResolver(id); if (n) classes.add(n); }
      });
      m.set(p.id, { cats: Array.from(cats), subs: Array.from(subs), classes: Array.from(classes) });
    });
    return m;
  }, [photos, resolveLabelCategory, resolveLabelSubcategory, labelNameResolver]);

  const labelCatOptions = useMemo(() => {
    const s = new Set<string>();
    photos.forEach((p) => (photoLabelInfo.get(p.id)?.cats || []).forEach((c) => s.add(c)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [photos, photoLabelInfo]);

  const labelSubOptions = useMemo(() => {
    const s = new Set<string>();
    photos.forEach((p) => {
      const info = photoLabelInfo.get(p.id); if (!info) return;
      if (labelCatFilter !== 'all' && !info.cats.includes(labelCatFilter)) return;
      info.subs.forEach((x) => s.add(x));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [photos, photoLabelInfo, labelCatFilter]);

  const labelClassOptions = useMemo(() => {
    const s = new Set<string>();
    photos.forEach((p) => {
      const info = photoLabelInfo.get(p.id); if (!info) return;
      if (labelCatFilter !== 'all' && !info.cats.includes(labelCatFilter)) return;
      if (labelSubFilter !== 'all' && !info.subs.includes(labelSubFilter)) return;
      info.classes.forEach((x) => s.add(x));
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [photos, photoLabelInfo, labelCatFilter, labelSubFilter]);

  const filteredPhotos = useMemo(() => {
    let list = availablePhotos
      .filter((p) => {
        if (mode === 'purchase' && p.unpriced) return false;
        return true;
      })
      .filter((p) => filters.category === 'all' || p.category === filters.category)
      .filter((p) => filters.subcategory === 'all' || p.subcategory === filters.subcategory)
      .filter((p) => filters.itemType === 'all' || p.itemType === filters.itemType)
      .filter((p) => filters.task === 'all' || p.taskTitle === filters.task);
    if (resolveLabelCategory) {
      list = list.filter((p) => {
        const info = photoLabelInfo.get(p.id);
        if (labelCatFilter !== 'all' && !(info?.cats || []).includes(labelCatFilter)) return false;
        if (labelSubFilter !== 'all' && !(info?.subs || []).includes(labelSubFilter)) return false;
        if (labelClassFilter !== 'all' && !(info?.classes || []).includes(labelClassFilter)) return false;
        return true;
      });
    }
    return list;
  }, [availablePhotos, filters, resolveLabelCategory, photoLabelInfo, labelCatFilter, labelSubFilter, labelClassFilter]);

  const browseStats = useMemo(() => {
    const categories = new Set<string>();
    const subcategories = new Set<string>();
    const tasks = new Set<string>();
    const labelClassIds = new Set<string>();
    let labels = 0;
    let credits = 0;
    let duration = 0;

    for (const p of filteredPhotos) {
      if (!isEmptyLevel(p.category)) categories.add(p.category);
      if (!isEmptyLevel(p.subcategory)) {
        subcategories.add(p.category + '||' + p.subcategory);
      }
      if (!isEmptyLevel(p.taskTitle)) {
        tasks.add(
          p.category + '||' + p.subcategory + '||' + p.itemType + '||' + p.taskTitle
        );
      }
      labels += typeof p.labelCount === 'number' ? p.labelCount : 0;
      if (Array.isArray(p.labelClassIds)) {
        for (const id of p.labelClassIds) {
          if (id) labelClassIds.add(id);
        }
      }
      credits += p.credits ?? 0;
      duration += typeof p.durationSeconds === 'number' ? p.durationSeconds : 0;
    }

    return {
      count: filteredPhotos.length,
      categories: categories.size,
      subcategories: subcategories.size,
      tasks: tasks.size,
      labels,
      labelTypes: labelClassIds.size,
      credits,
      duration,
    };
  }, [filteredPhotos]);

  const tree = useMemo<Node[]>(() => {
    const durationById = new Map<string, number>();
    for (const p of filteredPhotos) {
      durationById.set(p.id, typeof p.durationSeconds === 'number' ? p.durationSeconds : 0);
    }
    const aggDuration = (photoIds: string[]) => {
      let total = 0;
      for (const id of photoIds) total += durationById.get(id) || 0;
      return { durationTotal: total };
    };

    // Skip empty / Uncategorized levels so the tree collapses
    // (e.g. Trash + empty sub/item + task → Trash > task).

    type MNode = { photoIds: string[]; photos: RawPhoto[]; children: Map<string, MNode> };
    const ensureChild = (m: Map<string, MNode>, name: string): MNode => {
      let n = m.get(name);
      if (!n) {
        n = { photoIds: [], photos: [], children: new Map() };
        m.set(name, n);
      }
      return n;
    };

    const root = new Map<string, MNode>();
    for (const p of filteredPhotos) {
      const names = [p.category, p.subcategory, p.itemType, p.taskTitle].filter((n) => !isEmptyLevel(n));
      if (names.length === 0) names.push(FALLBACK);
      let cursor = root;
      let node: MNode | null = null;
      for (const name of names) {
        node = ensureChild(cursor, name);
        node.photoIds.push(p.id);
        cursor = node.children;
      }
      node!.photos.push(p);
    }

    const labelLookup = new Map<string, { classIds: string[]; count: number }>();
    if (labelNameResolver || resolveLabelCategory) {
      for (const p of filteredPhotos) {
        labelLookup.set(p.id, {
          classIds: p.labelClassIds || [],
          count: typeof p.labelCount === 'number' ? p.labelCount : (p.labelClassIds || []).length,
        });
      }
    }
    const aggLabels = (photoIds: string[]) => {
      if (!(labelNameResolver || resolveLabelCategory)) return {};
      const classIds = new Set<string>();
      let total = 0;
      for (const id of photoIds) {
        const info = labelLookup.get(id);
        if (!info) continue;
        info.classIds.forEach((c) => classIds.add(c));
        total += info.count;
      }
      return { labelTypes: classIds.size, labelTotal: total };
    };

    const sortEntries = (m: Map<string, MNode>) =>
      Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    /** Count leaf nodes (photo-bearing) under a subtree. */
    const countLeaves = (nodes: Node[]): number => {
      let n = 0;
      for (const c of nodes) {
        if (Array.isArray(c.photos)) n += 1;
        else n += countLeaves(c.children);
      }
      return n;
    };

    // level = depth after collapsing (0..k). key = "0/Name/1/Name/..." path.
    // Leaf = last non-empty taxonomy segment; holds photos[].
    const creditsById = new Map<string, number>();
    for (const p of filteredPhotos) {
      creditsById.set(p.id, typeof p.credits === 'number' ? p.credits : 0);
    }
    const aggCredits = (ids: string[]) => {
      let creditsTotal = 0;
      for (const id of ids) creditsTotal += creditsById.get(id) ?? 0;
      return { creditsTotal };
    };

    const toNodes = (m: Map<string, MNode>, keyPrefix: string, level: number): Node[] =>
      sortEntries(m).map(([name, raw]) => {
        const key = keyPrefix ? `${keyPrefix}/${level}/${name}` : `${level}/${name}`;
        const children = toNodes(raw.children, key, level + 1);
        const isLeaf = children.length === 0;
        const leafTasks = isLeaf ? 0 : countLeaves(children);
        const itemTypes = children.reduce((sum, c) => sum + c.children.length, 0);
        return {
          key,
          name,
          level,
          photoIds: raw.photoIds,
          ...(isLeaf ? { photos: raw.photos, children: [] as Node[] } : { children }),
          ...(!isLeaf
            ? {
                stats: {
                  subcategories: children.length,
                  itemTypes,
                  tasks: leafTasks,
                },
              }
            : {}),
          ...aggLabels(raw.photoIds),
          ...aggDuration(raw.photoIds),
          ...aggCredits(raw.photoIds),
        };
      });

    return toNodes(root, '', 0);
  }, [filteredPhotos, labelNameResolver, resolveLabelCategory]);

  const nodeState = (ids: string[]) => {
    if (ids.length === 0) return { checked: false, indeterminate: false };
    let s = 0; for (const id of ids) if (selected.has(id)) s++;
    return { checked: s === ids.length, indeterminate: s > 0 && s < ids.length };
  };

  const setFilter = (field: keyof Filters, value: string) => {
    const next: Filters = { ...filters, [field]: value };
    if (field === 'category') { next.subcategory = 'all'; next.itemType = 'all'; next.task = 'all'; }
    else if (field === 'subcategory') { next.itemType = 'all'; next.task = 'all'; }
    else if (field === 'itemType') { next.task = 'all'; }
    onFilterChange(next);
  };

  const rowStyle = (level: number, isSelected: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', paddingLeft: 16 + level * 26,
    borderBottom: `1px solid ${border}`,
    background: isSelected ? primary + '10' : (level === 0 ? card : 'transparent'),
    cursor: 'pointer',
  });

  const renderPhotos = (task: Node): React.ReactElement[] => (task.photos || []).map((ph) => {
    const isSel = selected.has(ph.id);
    const info = resolveLabelCategory ? photoLabelInfo.get(ph.id) : undefined;
    const classes = info?.classes || [];
    const labelTypes = classes.length;
    const labelTotal = ph.labelCount ?? classes.length;
    return (
      <div key={ph.id} style={rowStyle(4, isSel)} onClick={() => onToggleIds([ph.id], !isSel)}>
        <Check
          checked={isSel}
          indeterminate={false}
          disabled={!!ph.unpriced}
          onChange={() => { if (ph.unpriced) return; onToggleIds([ph.id], !isSel); }}
        />
        {ph.videoUrl ? (
          <button
            onClick={(e) => { e.stopPropagation(); if (ph.videoUrl) setPreview({ url: ph.videoUrl, title: ph.taskTitle }); }}
            disabled={!ph.videoUrl}
            title={mode === 'library' ? 'Play clip' : 'Preview clip'}
            style={{
              width: 120,
              height: 68,
              borderRadius: 6,
              border: `1px solid ${border}`,
              background: '#000',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: ph.videoUrl ? 'pointer' : 'not-allowed',
              opacity: ph.videoUrl ? 1 : 0.4,
              padding: 0,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>▶</span>
          </button>
        ) : ph.imageUrl ? (
          <img src={ph.imageUrl} alt={ph.taskTitle} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: `1px solid ${border}` }} />
        ) : (
          <div style={{ width: 44, height: 44, borderRadius: 6, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: secondary, fontSize: 11 }}>IMG</div>
        )}
        <span style={{ flex: 1, color: text, fontSize: 14, minWidth: 0 }}>
          <span>{ph.taskTitle}</span>
          {resolveLabelCategory && classes.length > 0 && (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 12, color: secondary, whiteSpace: 'nowrap' }}>
                {`${labelTypes} label type${labelTypes === 1 ? '' : 's'} · ${labelTotal} label${labelTotal === 1 ? '' : 's'}:`}
              </span>
              {classes.map((n, i) => (
                <span
                  key={`${ph.id}-lbl-${i}`}
                  style={{
                    display: 'inline-block',
                    border: `1px solid ${border}`,
                    borderRadius: 999,
                    padding: '1px 8px',
                    fontSize: 11,
                    color: secondary,
                    background: card,
                  }}
                >
                  {n}
                </span>
              ))}
            </span>
          )}
        </span>
        {durationEnabled && (
          <span style={{ color: secondary, fontSize: 13, marginRight: 12, whiteSpace: 'nowrap' }}>
            {fmtDuration(ph.durationSeconds ?? 0)}
          </span>
        )}
        {mode === 'library' ? (
          ph.exported ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ color: '#16a34a', fontWeight: 500 }}>Exported</span>
              {ph.lastExportedAt ? (
                <span style={{ color: secondary, fontSize: 12 }}>
                  {ph.lastExportedAt.toLocaleDateString()}
                </span>
              ) : null}
            </span>
          ) : (
            <span style={{ color: secondary, fontSize: 13 }}>Not exported</span>
          )
        ) : (
          <span style={{ color: secondary, fontSize: 13 }}>
            {ph.unpriced || typeof ph.credits !== 'number'
              ? 'No price'
              : `${ph.credits.toLocaleString()} credit${ph.credits === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
    );
  });

  const renderNodes = (nodes: Node[]): React.ReactElement[] => nodes.map((node) => {
    const st = nodeState(node.photoIds);
    const isOpen = expanded.has(node.key);
    // After collapse, leaf = node that holds photos (not always level 3)
    const isLeaf = Array.isArray(node.photos);
    const leafTaskId =
      isLeaf && node.photos && node.photos.length
        ? (node.photos[0].taskId || '').trim()
        : '';
    const leafThumb = leafTaskId ? taskThumbById[leafTaskId] || '' : '';
    return (
      <div key={node.key}>
        <div style={rowStyle(node.level, st.checked)} onClick={() => onToggleExpand(node.key)}>
          <Check checked={st.checked} indeterminate={st.indeterminate} onChange={() => onToggleIds(node.photoIds, !st.checked)} />
          <span style={{ width: 16, color: secondary }}>{isOpen ? '\u25be' : '\u25b8'}</span>
          {isLeaf ? (
              leafThumb ? (
                isVideoUrl(leafThumb) ? (
                  <video
                    src={`${leafThumb}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      border: `2px solid ${primary}`,
                      objectFit: 'cover',
                      marginRight: 8,
                      flexShrink: 0,
                      display: 'block',
                      background: '#000',
                    }}
                  />
                ) : (
                  <img
                    src={leafThumb}
                    alt=""
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      border: `2px solid ${primary}`,
                      objectFit: 'cover',
                      marginRight: 8,
                      flexShrink: 0,
                      display: 'block',
                    }}
                  />
                )
              ) : null
          ) : null}
          <span style={{ flex: 1, fontWeight: node.level === 0 ? 600 : isLeaf ? 400 : 500, color: text }}>{node.name}</span>
          {!isLeaf && node.stats && (
            <span style={{ color: secondary, fontSize: 12, marginRight: 16, whiteSpace: 'nowrap' }}>
              {node.level === 0 && `Subcategories: ${node.stats.subcategories} · Item Types: ${node.stats.itemTypes} · Tasks: ${node.stats.tasks}`}
              {node.level === 1 && `Item Types: ${node.stats.itemTypes} · Tasks: ${node.stats.tasks}`}
              {node.level >= 2 && `Tasks: ${node.stats.tasks}`}
            </span>
          )}
          {durationEnabled && !isLeaf && (
            <span style={{ color: secondary, fontSize: 12, marginRight: 16, whiteSpace: 'nowrap' }}>
              {`Total: ${fmtDuration(node.durationTotal ?? 0)}`}
            </span>
          )}
          {durationEnabled && isLeaf && (
            <span style={{ color: secondary, fontSize: 13, marginRight: 12, whiteSpace: 'nowrap' }}>
              {fmtDuration(node.durationTotal ?? 0)}
            </span>
          )}
          {(labelNameResolver || resolveLabelCategory) && (node.labelTotal ?? 0) > 0 && (
            <span style={{ color: secondary, fontSize: 12, marginRight: 16, whiteSpace: 'nowrap' }}>
              {`${node.labelTypes} label types · ${node.labelTotal} labels`}
            </span>
          )}
          <span style={{ color: secondary, fontSize: 14, minWidth: 90, textAlign: 'right' }}>{node.photoIds.length.toLocaleString()} {unitSingular}{node.photoIds.length === 1 ? '' : 's'}</span>
          {mode !== 'library' && (
            <span style={{ color: primary, fontSize: 14, fontWeight: 600, minWidth: 110, textAlign: 'right', marginLeft: 16 }}>{(node.creditsTotal ?? 0).toLocaleString()} credits</span>
          )}
        </div>
        {isOpen && (isLeaf ? renderPhotos(node) : renderNodes(node.children))}
      </div>
    );
  });

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, color: secondary, marginBottom: 4 };
  const selectStyle: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, border: `1px solid ${border}`, background: card, color: text, minWidth: 160 };

  const dropdown = (label: string, field: keyof Filters, value: string, options: string[], allLabel: string) => (
    <div className="filter-group">
      <label style={labelStyle}>{label}</label>
      <select style={selectStyle} value={value} onChange={(e) => setFilter(field, e.target.value)}>
        <option value="all">{allLabel}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const labelDropdown = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    options: string[],
    allLabel: string,
  ) => (
    <div className="filter-group">
      <label style={labelStyle}>{label}</label>
      <select style={selectStyle} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="all">{allLabel}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const inventoryTiles: Array<{ label: string; value: string }> = [
    { label: unitPluralCap, value: browseStats.count.toLocaleString() },
    { label: 'Categories', value: browseStats.categories.toLocaleString() },
    { label: 'Subcategories', value: browseStats.subcategories.toLocaleString() },
    { label: 'Tasks', value: browseStats.tasks.toLocaleString() },
  ];
  if (labelNameResolver || resolveLabelCategory) {
    inventoryTiles.push({ label: 'Labels', value: browseStats.labels.toLocaleString() });
  }
  if (durationEnabled) {
    inventoryTiles.push({ label: 'Total Duration', value: fmtDuration(browseStats.duration) });
  }
  if (mode === 'purchase') {
    inventoryTiles.push({ label: 'Credits', value: browseStats.credits.toLocaleString() });
  }

  return (
    <div style={{ marginBottom: 120 }}>
      {!loading && photos.length > 0 && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
          {inventoryTiles.map(({ label, value }) => (
            <div
              key={label}
              style={{
                flex: '1 1 150px',
                minWidth: 0,
                border: `1px solid ${border}`,
                borderRadius: 12,
                padding: '16px 18px',
                background: card,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: secondary,
                  marginBottom: 6,
                  lineHeight: 1.25,
                }}
              >
                {label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: text, lineHeight: 1.1 }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="filters-row" style={{ marginBottom: resolveLabelCategory ? 12 : 20 }}>
        {dropdown('Category', 'category', filters.category, categoryOptions, 'All Categories')}
        {dropdown('Subcategory', 'subcategory', filters.subcategory, subcategoryOptions, 'All Subcategories')}
        {dropdown('Item Type', 'itemType', filters.itemType, itemTypeOptions, 'All Item Types')}
        {dropdown('Task', 'task', filters.task, taskOptions, 'All Tasks')}
      </div>

      {resolveLabelCategory && (
        <>
          <div style={{ borderTop: `1px solid ${border}`, margin: '16px 0' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: text, marginBottom: 10 }}>Annotation Labels</div>
          <div className="filters-row" style={{ marginBottom: 20 }}>
            {labelDropdown(
              'Label Category',
              labelCatFilter,
              (v) => { setLabelCatFilter(v); setLabelSubFilter('all'); setLabelClassFilter('all'); },
              labelCatOptions,
              'All Label Categories',
            )}
            {labelDropdown(
              'Label Subcategory',
              labelSubFilter,
              (v) => { setLabelSubFilter(v); setLabelClassFilter('all'); },
              labelSubOptions,
              'All Label Subcategories',
            )}
            {labelDropdown(
              'Label Class',
              labelClassFilter,
              setLabelClassFilter,
              labelClassOptions,
              'All Labels',
            )}
          </div>
        </>
      )}

      {loading ? (
        <div className="loading" style={{ color: secondary }}>Loading released photos…</div>
      ) : filteredPhotos.length === 0 ? (
        <div className="empty-state" style={{ color: secondary }}>
          {photos.length === 0 ? 'No raw photos have been released to the marketplace yet.' : 'No photos match the selected filters.'}
        </div>
      ) : (
        <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `2px solid ${border}`, fontWeight: 600, color: text, background: card }}>
            <Check checked={headerCheckState.checked} indeterminate={headerCheckState.indeterminate} onChange={toggleAll} />
            <span style={{ width: 16 }} />
            <span style={{ flex: 1 }}>Category / Subcategory / Item / Task</span>
            <span style={{ minWidth: 90, textAlign: 'right' }}>{unitPluralCap}</span>
            {mode !== 'library' && (
              <span style={{ minWidth: 110, textAlign: 'right', marginLeft: 16 }}>Credits</span>
            )}
          </div>
          {renderNodes(tree)}
        </div>
      )}

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: card,
              border: `1px solid ${border}`,
              borderRadius: 12,
              padding: 16,
              maxWidth: 720,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <strong style={{ color: text, fontSize: 15 }}>{preview.title}</strong>
              <button
                onClick={() => setPreview(null)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', color: secondary, fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: '0 4px' }}
              >
                ×
              </button>
            </div>
              {mode === 'library' ? (
                <>
                  <video
                    ref={previewVideoRef}
                    src={preview.url}
                    autoPlay
                    controls
                    playsInline
                    style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block' }}
                  />
                  <div style={{ color: secondary, fontSize: 12 }}>
                    Full clip — you own this video.
                  </div>
                </>
              ) : (
                <>
                  <video
                    ref={previewVideoRef}
                    src={`${preview.url}#t=0,10`}
                    autoPlay
                    playsInline
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    onPlay={() => setPreviewPlaying(true)}
                    onPause={() => setPreviewPlaying(false)}
                    onTimeUpdate={(e) => {
                      const v = e.currentTarget;
                      if (v.currentTime >= 10) {
                        v.pause();
                        v.currentTime = 0;
                        setPreviewTime(0);
                        setPreviewPlaying(false);
                        return;
                      }
                      setPreviewTime(v.currentTime);
                    }}
                    onEnded={() => { setPreviewPlaying(false); setPreviewTime(0); }}
                    style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block' }}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                      onClick={() => {
                        const v = previewVideoRef.current;
                        if (!v) return;
                        if (v.paused) { v.play(); } else { v.pause(); }
                      }}
                      aria-label={previewPlaying ? 'Pause' : 'Play'}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: `1px solid ${border}`,
                        background: 'none',
                        color: text,
                        cursor: 'pointer',
                        fontSize: 13,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {previewPlaying ? '❚❚' : '▶'}
                    </button>

                    <div style={{ flex: 1, height: 4, borderRadius: 999, background: border, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.min(100, (previewTime / 10) * 100)}%`,
                          height: '100%',
                          background: primary,
                          transition: 'width 0.1s linear',
                        }}
                      />
                    </div>

                    <span style={{ color: secondary, fontSize: 12, minWidth: 34, textAlign: 'right' }}>
                      {previewTime.toFixed(1)}s
                    </span>
                  </div>

                  <div style={{ color: secondary, fontSize: 12 }}>
                    10-second preview. The full clip is included when you purchase.
                  </div>
                </>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
